import { z } from "zod"

import { ROLE_SYSTEM_PROMPT_MAX_LENGTH } from "@/lib/ai/roles/types"
import {
  DEFAULT_ONBOARDING_LANGUAGES,
  SignupOnboardingLanguagesSchema,
} from "@/lib/onboarding/languages"

export const SIGNUP_ONBOARDING_VERSION = 1
export const SIGNUP_ONBOARDING_BUSINESS_SUMMARY_MAX_LENGTH = 12_000

export const SIGNUP_ONBOARDING_STEPS = [
  "website",
  "agent-types",
  "prompt",
  "test",
  "channel",
  "review",
] as const

export type SignupOnboardingStep = (typeof SIGNUP_ONBOARDING_STEPS)[number]
export type SignupAgentType = "messaging" | "email" | "voice"
export type SignupOnboardingSocialKnowledgeSource = "instagram" | "facebook"
export type SignupOnboardingKnowledgeSource =
  | "not_selected"
  | "website"
  | "documents"
  | SignupOnboardingSocialKnowledgeSource
  | "skipped"
  | "shopify"

export const SIGNUP_ONBOARDING_STEP_LABELS: Record<
  SignupOnboardingStep,
  string
> = {
  website: "Knowledge",
  "agent-types": "Agent selection",
  prompt: "Prompt personalization",
  test: "Test agent",
  channel: "Channel connection",
  review: "Review",
}

const PromptBundleSchema = z.record(
  z.string(),
  z.object({
    system_prompt: z.string().max(ROLE_SYSTEM_PROMPT_MAX_LENGTH),
    settings: z.record(z.string(), z.unknown()).default({}),
  }),
)

export const SignupOnboardingAgentSchema = z.object({
  client_id: z.string().uuid(),
  type: z.enum(["messaging", "email", "voice"]),
  roles: z.array(z.string().trim().min(1)).min(1).max(3),
  role_label: z.string().trim().max(60).nullable().default(null),
  role_names: z.array(z.string().trim().min(1)).min(1).max(3),
  name: z.string().trim().min(1).max(100),
  name_edited: z.boolean().default(false),
  composed: PromptBundleSchema.optional(),
  original_prompt: z.string().max(ROLE_SYSTEM_PROMPT_MAX_LENGTH).nullable().default(null),
  enriched_prompt: z.string().max(ROLE_SYSTEM_PROMPT_MAX_LENGTH).nullable().default(null),
  edited_enriched_prompt: z.string().max(ROLE_SYSTEM_PROMPT_MAX_LENGTH).nullable().default(null),
  business_summary: z
    .string()
    .max(SIGNUP_ONBOARDING_BUSINESS_SUMMARY_MAX_LENGTH)
    .nullable()
    .default(null),
  languages: SignupOnboardingLanguagesSchema.default([
    ...DEFAULT_ONBOARDING_LANGUAGES,
  ]),
  opening_message: z
    .string()
    .trim()
    .min(1)
    .max(1000)
    .nullable()
    .default(null),
  suggested_questions: z
    .array(z.string().trim().min(1).max(160))
    .max(5)
    .default([]),
  approved_prompt: z.string().max(ROLE_SYSTEM_PROMPT_MAX_LENGTH).nullable().default(null),
  prompt_choice: z.enum(["original", "enriched"]).nullable().default(null),
  telephony_choice: z.enum(["twilio", "sip", "later"]).nullable().optional(),
  telephony_identifier: z.string().trim().max(255).nullable().optional(),
  agent_id: z.string().uuid().nullable().default(null),
})

export type SignupOnboardingAgent = z.infer<typeof SignupOnboardingAgentSchema>

const SignupOnboardingStepSchema = z.preprocess(
  (step) => (step === "agent-details" ? "prompt" : step),
  z.enum(SIGNUP_ONBOARDING_STEPS),
)

export const SignupOnboardingStateSchema = z.object({
  version: z.literal(SIGNUP_ONBOARDING_VERSION),
  status: z.enum(["in_progress", "completed", "skipped"]),
  current_step: SignupOnboardingStepSchema,
  active_agent_index: z.number().int().min(0).max(5).default(0),
  website: z.object({
    status: z.enum(["not_started", "importing", "completed", "failed", "skipped"]),
    url: z.string().max(2048).nullable(),
    job_id: z.string().uuid().nullable(),
  }),
  social: z.object({
    status: z.enum(["not_started", "importing", "completed", "failed", "skipped"]),
    url: z.string().max(2048).nullable(),
    job_id: z.string().uuid().nullable(),
  }),
  knowledge_source: z
    .enum([
      "not_selected",
      "website",
      "documents",
      "instagram",
      "facebook",
      "skipped",
      "shopify",
    ])
    .default("not_selected"),
  /** Server-owned: the connected store this onboarding started from. */
  shopify_shop_domain: z.string().trim().max(255).nullable().default(null),
  agents: z.array(SignupOnboardingAgentSchema).max(6),
  completed_at: z.string().datetime().nullable(),
  skipped_at: z.string().datetime().nullable(),
  resume_prompt_dismissed: z.boolean(),
})

export type SignupOnboardingState = z.infer<typeof SignupOnboardingStateSchema>

export function createInitialSignupOnboardingState(
  options?: { shopifyShopDomain?: string | null },
): SignupOnboardingState {
  return {
    version: SIGNUP_ONBOARDING_VERSION,
    status: "in_progress",
    current_step: "website",
    active_agent_index: 0,
    website: {
      status: "not_started",
      url: null,
      job_id: null,
    },
    social: {
      status: "not_started",
      url: null,
      job_id: null,
    },
    knowledge_source: "not_selected",
    shopify_shop_domain: options?.shopifyShopDomain ?? null,
    agents: [],
    completed_at: null,
    skipped_at: null,
    resume_prompt_dismissed: false,
  }
}

export function createCompletedSignupOnboardingState(): SignupOnboardingState {
  return {
    ...createInitialSignupOnboardingState(),
    status: "completed",
    current_step: "review",
  }
}

export function getSignupOnboardingStepIndex(step: SignupOnboardingStep): number {
  return SIGNUP_ONBOARDING_STEPS.indexOf(step)
}

export type SignupOnboardingAgentStep = "prompt" | "test" | "channel"

export function getSignupAgentTypeLabel(type: SignupAgentType): string {
  if (type === "messaging") return "Messaging Agent"
  if (type === "email") return "Email Agent"
  return "Phone Voice Agent"
}

export function upsertSignupOnboardingAgent(
  state: SignupOnboardingState,
  agent: SignupOnboardingAgent,
): SignupOnboardingState {
  const index = state.agents.findIndex((item) => item.client_id === agent.client_id)
  if (index < 0) return { ...state, agents: [...state.agents, agent] }

  const agents = [...state.agents]
  agents[index] = agent
  return { ...state, agents }
}

export function getSignupOnboardingReadyWebsitePageCount(job: {
  result: Record<string, unknown> | null
  processed_count: number
} | null): number {
  if (!job) return 0
  const documents = Array.isArray(job.result?.documents)
    ? job.result.documents.length
    : 0
  const imported = typeof job.result?.imported === "number" ? job.result.imported : 0
  const updated = typeof job.result?.updated === "number" ? job.result.updated : 0
  const unchanged = typeof job.result?.unchanged === "number" ? job.result.unchanged : 0
  return documents || imported + updated + unchanged
}

export function hasSignupOnboardingKnowledge(
  state: Pick<SignupOnboardingState, "website" | "social" | "knowledge_source">,
): boolean {
  switch (state.knowledge_source) {
    // Read on demand — nothing to wait for.
    case "documents":
    case "shopify":
      return true
    case "website":
      return Boolean(state.website.job_id)
    case "instagram":
    case "facebook":
      return Boolean(state.social.job_id)
    case "not_selected":
    case "skipped":
      return false
    default: {
      const exhaustiveSource: never = state.knowledge_source
      return exhaustiveSource
    }
  }
}

/** The one import a source can be waiting on, whichever kind of import it is. */
export function getSignupOnboardingImportJobId(
  state: Pick<SignupOnboardingState, "website" | "social" | "knowledge_source">,
): string | null {
  return isSignupOnboardingSocialKnowledgeSource(state.knowledge_source)
    ? state.social.job_id
    : state.website.job_id
}

export function isSignupOnboardingSocialKnowledgeSource(
  source: SignupOnboardingKnowledgeSource,
): source is SignupOnboardingSocialKnowledgeSource {
  return source === "instagram" || source === "facebook"
}

export function parseSignupOnboardingBusinessSummary(
  summary: string | null,
): string[] {
  if (!summary) return []

  return summary
    .split(/\r?\n/)
    .map((item) =>
      item
        .trim()
        .replace(/^(?:[-*•]+|\d+[.)])\s*/, "")
        .trim(),
    )
    .filter(Boolean)
}

export function formatSignupOnboardingBusinessSummary(
  items: readonly string[],
): string {
  return items
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => `- ${item}`)
    .join("\n")
}

function normalizeNamePart(value: string): string {
  return value.trim().replace(/\s+/g, " ").replace(/[\s,.;:!?-]+$/g, "")
}

function joinRoleNames(roleNames: readonly string[]): string {
  const names = roleNames.map(normalizeNamePart).filter(Boolean)
  if (names.length <= 1) return names[0] ?? "Custom"
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
}

export function suggestSignupAgentName(companyName: string, roleNames: readonly string[]): string {
  const company = normalizeNamePart(companyName)
  const roles = joinRoleNames(roleNames)
  return [company, roles, "Agent"].filter(Boolean).join(" ")
}

export function shouldShowSignupOnboardingResumePrompt(
  state: SignupOnboardingState | null,
  now = new Date(),
): boolean {
  if (
    !state ||
    state.status !== "skipped" ||
    state.resume_prompt_dismissed ||
    !state.skipped_at
  ) {
    return false
  }

  const skippedAt = new Date(state.skipped_at)
  if (Number.isNaN(skippedAt.getTime())) return false
  return now.getTime() - skippedAt.getTime() <= 5 * 24 * 60 * 60 * 1000
}
